import { ANON_IDENTITY } from '@jbroll/rowboat-client';

interface Keyed {
  itemId: string;
}

export interface JobRecords<J extends Keyed> {
  get(itemId: string): Promise<J | undefined>;
  put(job: J): Promise<void>;
  delete(itemId: string): Promise<void>;
  list(): Promise<J[]>;
  /** The records as scoped now; a later `bind` does not move them. */
  pass(): JobRecords<J>;
  owner(): string | undefined;
}

interface JobBase<J extends Keyed> extends Omit<JobRecords<J>, 'pass' | 'owner'> {
  /** Writes `job` unless a job with its key exists; resolves whether it wrote. */
  putIfAbsent(job: J): Promise<boolean>;
}

export interface OwnedJobStore<J extends Keyed> extends JobRecords<J> {
  /** Scopes every later call to `owner`; `holds` says whether an item row is in that identity's store. */
  bind(owner: string, holds: (itemId: string) => boolean): void;
}

const OWNER_SEP = ':';

interface Scope {
  owner: string;
  prefix: string;
  holds: (itemId: string) => boolean;
  /** Base keys of adoptable jobs by item, listed on the first call after the bind. */
  adoptable?: Promise<Map<string, string[]>>;
}

/**
 * Jobs are keyed `<owner>:<itemId>`, so an account never sees or runs jobs another account queued in
 * this browser. Jobs queued anonymously, and unprefixed ones from before keys carried an owner,
 * follow their item: once this identity's store holds the item's row, the next call moves them here.
 */
export function ownedJobStore<J extends Keyed>(base: JobBase<J>): OwnedJobStore<J> {
  let scope: Scope | undefined;
  const bound = () => {
    if (!scope) throw new Error('identify jobs used before an owner was bound');
    return view(scope);
  };

  const strip = (job: J, prefix: string): J => ({
    ...job,
    itemId: job.itemId.slice(prefix.length),
  });

  const listAdoptable = async (prefix: string) => {
    const byItem = new Map<string, string[]>();
    for (const { itemId: key } of await base.list()) {
      if (key.startsWith(prefix)) continue;
      const sep = key.indexOf(OWNER_SEP);
      if (sep >= 0 && key.slice(0, sep) !== ANON_IDENTITY) continue;
      const itemId = key.slice(sep + 1);
      byItem.set(itemId, [...(byItem.get(itemId) ?? []), key]);
    }
    return byItem;
  };

  function view(current: Scope): JobRecords<J> {
    const { owner, prefix, holds } = current;
    const adopt = async (only?: string) => {
      current.adoptable ??= listAdoptable(prefix).catch((err: unknown) => {
        current.adoptable = undefined;
        throw err;
      });
      const byItem = await current.adoptable;
      const itemIds = only === undefined ? [...byItem.keys()] : byItem.has(only) ? [only] : [];
      for (const itemId of itemIds) {
        const keys = byItem.get(itemId);
        if (!keys || !holds(itemId)) continue;
        byItem.delete(itemId);
        for (const key of keys) {
          const job = await base.get(key);
          if (job) await base.putIfAbsent({ ...job, itemId: prefix + itemId });
          await base.delete(key);
        }
      }
    };
    const records: JobRecords<J> = {
      async get(itemId) {
        await adopt(itemId);
        const job = await base.get(prefix + itemId);
        return job && strip(job, prefix);
      },
      async put(job) {
        await base.put({ ...job, itemId: prefix + job.itemId });
      },
      async delete(itemId) {
        await base.delete(prefix + itemId);
      },
      async list() {
        await adopt();
        return (await base.list())
          .filter((j) => j.itemId.startsWith(prefix))
          .map((j) => strip(j, prefix));
      },
      pass: () => records,
      owner: () => owner,
    };
    return records;
  }

  return {
    bind(owner, holds) {
      scope = { owner, prefix: `${owner}${OWNER_SEP}`, holds };
    },
    get: async (itemId) => bound().get(itemId),
    put: async (job) => bound().put(job),
    delete: async (itemId) => bound().delete(itemId),
    list: async () => bound().list(),
    pass: () => bound(),
    owner: () => bound().owner(),
  };
}
